/*
 * Mise à jour du Master par liaison série (USB).
 *
 * Contrairement au flux BLE des cibles et carabines, on ne parle pas au firmware
 * du Master : esptool-js s'adresse au bootloader ROM de l'ESP32, gravé en usine.
 * Aucune coopération du firmware en place n'est donc nécessaire, ce qui permet de
 * mettre à jour les Masters déjà en circulation, qui ne savent pas se mettre à
 * jour tout seuls.
 *
 * Conséquence : il faut un vrai port USB. Une liaison série par Bluetooth ne
 * fonctionnerait pas (pas de DTR/RTS pour basculer en bootloader).
 */

import React, { useState, useEffect, useRef } from "react";
import {
  IonContent,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonCardSubtitle,
  IonSelect,
  IonSelectOption,
  IonText,
  IonGrid,
  IonRow,
} from "@ionic/react";
import { ESPLoader, Transport, IEspLoaderTerminal } from "esptool-js";
import ProgressBar from "@ramonak/react-progress-bar";

// Dossier FTP où deploy.js (projet BzookMaster) dépose les firmware_X.Y.bin
const URL_BINAIRES = "https://ota.bzook.fr/binaires/MASTER/";

// Seule l'application est réécrite. Le bootloader (0x1000) et la table de
// partitions (0x8000) sont identiques sur tout le parc et ne sont pas touchés :
// moins de données à transférer, et rien à recréer en cas d'échec.
const ADRESSE_APPLICATION = 0x10000;

// 921600 fait décrocher les puces CH340 des DevKit clones ; l'écart de durée
// sur ~700 Ko est marginal.
const DEBIT_FLASH = 460800;

// Extrait un numéro de version d'un nom de fichier ("firmware_0.4.bin" -> "0.4")
const extraireVersion = (s?: string | null): string | null => {
  if (!s) return null;
  const m = s.match(/(\d+(?:\.\d+)+)/);
  return m ? m[1] : null;
};

// Trie les fichiers par version décroissante, pour proposer la plus récente
const trierParVersion = (fichiers: string[]): string[] =>
  [...fichiers].sort((a, b) => {
    const va = extraireVersion(a) ?? "0";
    const vb = extraireVersion(b) ?? "0";
    return vb.localeCompare(va, undefined, { numeric: true });
  });

type Etat = "repos" | "encours" | "ok" | "erreur";

const FlashMaster: React.FC<{ onFermer: () => void }> = ({ onFermer }) => {
  const [fichiers, setFichiers] = useState<string[]>([]);
  const [fichier, setFichier] = useState<string | null>(null);
  const [etat, setEtat] = useState<Etat>("repos");
  const [message, setMessage] = useState<string>("");
  const [progression, setProgression] = useState(0);
  const [journal, setJournal] = useState<string[]>([]);

  const finJournal = useRef<HTMLDivElement | null>(null);
  const serieDisponible = typeof navigator !== "undefined" && "serial" in navigator;

  const journaliser = (ligne: string) =>
    setJournal((j) => [...j, ligne].slice(-300));

  // Terminal branché sur esptool-js. write() reçoit des fragments sans retour à
  // la ligne (barre de progression interne) : on les recolle sur la ligne courante.
  const terminal = useRef<IEspLoaderTerminal>({
    clean: () => setJournal([]),
    writeLine: (donnee: string) => setJournal((j) => [...j, donnee].slice(-300)),
    write: (donnee: string) =>
      setJournal((j) => {
        const copie = j.length ? [...j] : [""];
        copie[copie.length - 1] += donnee;
        return copie.slice(-300);
      }),
  }).current;

  // Liste des firmwares disponibles (listing HTML du dossier, comme le flux BLE)
  useEffect(() => {
    fetch(URL_BINAIRES)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const liens = Array.from(doc.querySelectorAll("a"))
          .map((a) => a.getAttribute("href"))
          .filter((h): h is string => !!h && h.endsWith(".bin"));
        const tries = trierParVersion(liens);
        setFichiers(tries);
        if (tries.length > 0) setFichier(tries[0]);
        else setMessage("Aucun firmware trouvé dans " + URL_BINAIRES);
      })
      .catch((e) => setMessage("Listing des firmwares impossible : " + e.message));
  }, []);

  // Garde le journal collé en bas
  useEffect(() => {
    finJournal.current?.scrollIntoView({ block: "end" });
  }, [journal]);

  const flasher = async () => {
    if (!fichier) return;

    setEtat("encours");
    setProgression(0);
    setMessage("");
    setJournal([]);

    let transport: Transport | null = null;
    const debut = Date.now();

    try {
      // Le firmware est récupéré AVANT d'ouvrir le port : si le réseau échoue,
      // le Master n'a pas encore été basculé en mode bootloader.
      journaliser(`Téléchargement de ${fichier}…`);
      const reponse = await fetch(`${URL_BINAIRES}${fichier}`);
      if (!reponse.ok) {
        throw new Error(`téléchargement impossible (HTTP ${reponse.status})`);
      }
      const image = new Uint8Array(await reponse.arrayBuffer());
      journaliser(`Firmware chargé : ${(image.length / 1024).toFixed(0)} Ko`);

      // requestPort() exige un geste utilisateur : on est bien dans un onClick
      const port = await navigator.serial.requestPort();
      transport = new Transport(port, true);

      const loader = new ESPLoader({
        transport,
        baudrate: DEBIT_FLASH,
        terminal,
        debugLogging: false,
      });

      // main() fait la séquence DTR/RTS qui bascule l'ESP32 en bootloader,
      // puis identifie la puce et monte le débit
      const puce = await loader.main();
      journaliser(`Puce détectée : ${puce}`);

      await loader.writeFlash({
        fileArray: [{ data: image, address: ADRESSE_APPLICATION }],
        flashMode: "dio",
        flashFreq: "40m",
        flashSize: "4MB",
        eraseAll: false,
        compress: true,
        reportProgress: (_index, ecrit, total) => {
          setProgression(Math.round((ecrit / total) * 100));
        },
      });

      // Redémarrage sur le firmware fraîchement écrit
      await loader.after("hard_reset");

      const duree = Math.round((Date.now() - debut) / 1000);
      setProgression(100);
      setEtat("ok");
      setMessage(
        `Master mis à jour en ${duree} s. Il redémarre en 9600 bauds, ` +
          `l'application course le retrouvera normalement.`
      );
    } catch (e: any) {
      const detail = e?.message ?? String(e);
      setEtat("erreur");
      setMessage(detail);
      journaliser(`❌ ${detail}`);
    } finally {
      // Toujours relâcher le port, sinon il reste verrouillé pour les autres pages
      try {
        await transport?.disconnect();
      } catch (e) {
        /* port déjà fermé */
      }
    }
  };

  const enCours = etat === "encours";
  const versionCible = extraireVersion(fichier);

  return (
    <IonContent class="ion-padding">
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>Mise à jour du Master (USB)</IonCardTitle>
          <IonCardSubtitle>
            Branchez le Master en USB. La mise à jour passe par le bootloader de
            l'ESP32 : elle fonctionne quel que soit le firmware déjà installé.
          </IonCardSubtitle>
        </IonCardHeader>

        <IonCardContent>
          {!serieDisponible && (
            <IonText color="danger">
              <p>
                Ce navigateur n'expose pas le port série. Utilisez le BZØØK
                Booster, ou Chrome / Edge sur ordinateur.
              </p>
            </IonText>
          )}

          <IonSelect
            value={fichier}
            placeholder="Choisir un firmware"
            disabled={enCours || fichiers.length === 0}
            onIonChange={(e) => setFichier(e.detail.value)}
          >
            {fichiers.map((f) => (
              <IonSelectOption key={f} value={f}>
                {f}
              </IonSelectOption>
            ))}
          </IonSelect>

          {versionCible && (
            <IonText>
              <p className="ota-target-version">🎯 Version cible : {versionCible}</p>
            </IonText>
          )}

          <IonButton
            onClick={flasher}
            disabled={!fichier || enCours || !serieDisponible}
          >
            {enCours ? "Mise à jour en cours…" : "Flasher le Master"}
          </IonButton>
          <IonButton color="medium" onClick={onFermer} disabled={enCours}>
            Retour
          </IonButton>

          {(enCours || progression > 0) && (
            <ProgressBar
              completed={progression}
              labelAlignment="center"
              bgColor="#3880ff"
            />
          )}

          {message && (
            <IonText color={etat === "erreur" ? "danger" : "success"}>
              <p>{message}</p>
            </IonText>
          )}

          {enCours && (
            <IonText color="warning">
              <p>
                Ne débranchez pas le Master. Une coupure n'abîme rien
                définitivement, mais il faudra recommencer.
              </p>
            </IonText>
          )}
        </IonCardContent>
      </IonCard>

      {journal.length > 0 && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Journal</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <pre className="ota-journal">
                  {journal.join("\n")}
                  <div ref={finJournal} />
                </pre>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>
      )}
    </IonContent>
  );
};

export default FlashMaster;
